import { ADMIN_NAMES } from '../config/teamConfig';
import { Employee } from '../types';

export type EmployeeOrg = 'elink' | 'phishingbox' | 'admin';

/**
 * Classify an employee as eLink Design, PhishingBox, or admin.
 * eLink names come from the scrum-tracking employee list (/api/employees).
 */
export function getEmployeeGroup(
  fullName: string,
  elinkEmployeeNames: Set<string>
): EmployeeOrg {
  const nameLower = fullName.toLowerCase().trim();

  if (ADMIN_NAMES.includes(nameLower)) {
    return 'admin';
  }

  const firstName = nameLower.split(' ')[0];
  const elinkNamesArray = Array.from(elinkEmployeeNames);

  for (let i = 0; i < elinkNamesArray.length; i++) {
    const elinkName = elinkNamesArray[i];
    if (firstName === elinkName || nameLower.startsWith(elinkName + ' ') || nameLower === elinkName) {
      return 'elink';
    }
  }

  return 'phishingbox';
}

/** Theme org key — admins use the eLink palette. */
export function getThemeOrg(group: EmployeeOrg): 'elink' | 'phishingbox' {
  return group === 'phishingbox' ? 'phishingbox' : 'elink';
}

export type PerformanceTeamFilter = 'elink' | 'phishingbox';

/** Match intranet developer name to a performance-tracker employee row. */
export function findEmployeeByName(employees: Employee[], name: string): Employee | undefined {
  const normalized = name.toLowerCase().trim();
  const exact = employees.find((employee) => employee.name.toLowerCase().trim() === normalized);
  if (exact) return exact;

  const parts = normalized.split(' ').filter(Boolean);
  if (parts.length < 2) return undefined;

  const firstName = parts[0];
  const lastName = parts[parts.length - 1];

  return employees.find((employee) => {
    const employeeParts = employee.name.toLowerCase().trim().split(' ').filter(Boolean);
    if (employeeParts.length < 2) return false;
    return employeeParts[0] === firstName && employeeParts[employeeParts.length - 1] === lastName;
  });
}

interface IntranetDeveloperLike {
  id: string;
  name: string;
}

interface BuildPerformanceTeamListOptions {
  employees: Employee[];
  intranetDevelopers: IntranetDeveloperLike[];
  teamFilter: PerformanceTeamFilter;
  archivedIntranetKeys?: Set<string>;
}

function isArchivedIntranetDeveloper(
  developer: IntranetDeveloperLike,
  archivedIntranetKeys?: Set<string>
): boolean {
  if (!archivedIntranetKeys || archivedIntranetKeys.size === 0) {
    return false;
  }

  if (archivedIntranetKeys.has(String(developer.id))) {
    return true;
  }

  return archivedIntranetKeys.has(developer.name.toLowerCase().trim());
}

/**
 * eLink roster names come from the performance DB (/api/employees).
 * PhishingBox tab uses intranet developers not on that roster.
 */
export function buildPerformanceTeamList({
  employees,
  intranetDevelopers,
  teamFilter,
  archivedIntranetKeys,
}: BuildPerformanceTeamListOptions): Employee[] {
  const elinkRoster = employees.filter(
    (employee) => (employee.department || '').toLowerCase() !== 'phishingbox'
  );
  const elinkNames = new Set(elinkRoster.map((employee) => employee.name.trim().toLowerCase()));

  if (teamFilter === 'elink') {
    return elinkRoster;
  }

  const phishingboxFromDb = employees.filter(
    (employee) => (employee.department || '').toLowerCase() === 'phishingbox'
  );
  const phishingboxDevs = intranetDevelopers.filter(
    (developer) =>
      getEmployeeGroup(developer.name, elinkNames) === 'phishingbox' &&
      !isArchivedIntranetDeveloper(developer, archivedIntranetKeys)
  );

  const findLinkedEmployee = (developer: IntranetDeveloperLike): Employee | undefined => {
    const byIntranetId = employees.find(
      (employee) => employee.intranet_id && String(employee.intranet_id) === String(developer.id)
    );
    if (byIntranetId) return byIntranetId;

    return (
      findEmployeeByName(phishingboxFromDb, developer.name) ??
      findEmployeeByName(employees, developer.name)
    );
  };

  const merged = phishingboxDevs.map((developer) => {
    const existing = findLinkedEmployee(developer);
    if (existing) return existing;

    return {
      id: -Number.parseInt(developer.id, 10) || -developer.name.length,
      name: developer.name,
      department: 'PhishingBox',
      position: 'Developer',
      points: 10,
      performance_score: null,
      notes: null,
      intranet_id: developer.id,
    };
  });

  phishingboxFromDb.forEach((employee) => {
    if (!merged.some((row) => row.id === employee.id)) {
      merged.push(employee);
    }
  });

  return merged.sort((a, b) => a.name.localeCompare(b.name));
}
